import React, { useState, useEffect } from 'react';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Star, User, Save } from 'lucide-react';

function JudgingPanel() {
  const [user, setUser] = useState(null);
  const [artists, setArtists] = useState([]);
  const [ratings, setRatings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAccess();
    fetchArtists();
  }, []);

  const checkAccess = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      const data = await response.json();
      
      if (!response.ok || (!data.is_admin && !data.is_judge)) {
        toast.error('Access denied. Only judges and admins can access this page.');
        navigate('/dashboard');
        return;
      }
      
      setUser(data);
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchArtists = async () => {
    try {
      const response = await fetch(`${API}/users?is_artist=true`);
      const data = await response.json();
      setArtists(data);
      
      // Initialize ratings with default value of 3
      const initialRatings = {};
      data.forEach(artist => {
        initialRatings[artist.user_id] = 3;
      });
      setRatings(initialRatings);
    } catch (error) {
      toast.error('Failed to load artists');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRatingChange = (artistId, value) => {
    setRatings({
      ...ratings,
      [artistId]: parseInt(value)
    });
  };

  const submitRating = async (artistId) => {
    try {
      const response = await fetch(`${API}/judge-votes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          artist_id: artistId,
          event_id: 'default_event',
          rating: ratings[artistId],
          attendance_status: 'present'
        })
      });

      if (response.ok) {
        toast.success(`Rating ${ratings[artistId]}/10 submitted successfully!`);
      } else {
        toast.error('Failed to submit rating');
      }
    } catch (error) {
      toast.error('Failed to submit rating');
    }
  };

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen pb-24">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
          ← Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1 data-testid="judging-panel-title" className="text-4xl md:text-5xl font-bold mb-2 tracking-tight">
            Judging Panel
          </h1>
          <p className="text-muted-foreground">Rate artists on their performance</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {artists.length === 0 ? (
            <Card className="glass-panel p-12 text-center col-span-full">
              <p className="text-muted-foreground">No artists registered yet</p>
            </Card>
          ) : (
            artists.map((artist) => (
              <Card
                key={artist.user_id}
                className="glass-panel p-6"
                data-testid={`artist-judge-card-${artist.user_id}`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-primary/20 mb-4">
                    {artist.picture ? (
                      <img src={artist.picture} alt={artist.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="h-12 w-12 text-primary" />
                      </div>
                    )}
                  </div>
                  
                  <h3 className="text-xl font-bold mb-2">{artist.stage_name || artist.name}</h3>
                  
                  {artist.talents && artist.talents.length > 0 && (
                    <div className="flex flex-wrap gap-2 justify-center mb-4">
                      {artist.talents.slice(0, 2).map((talent, idx) => (
                        <span key={idx} className="px-2 py-1 bg-primary/20 rounded-full text-xs">
                          {talent}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="w-full space-y-4 mt-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        Rating: {ratings[artist.user_id]}/10
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={ratings[artist.user_id]}
                        onChange={(e) => handleRatingChange(artist.user_id, e.target.value)}
                        className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #7c3aed 0%, #7c3aed ${(ratings[artist.user_id] / 10) * 100}%, rgba(255,255,255,0.1) ${(ratings[artist.user_id] / 10) * 100}%, rgba(255,255,255,0.1) 100%)`
                        }}
                        data-testid={`rating-slider-${artist.user_id}`}
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>1</span>
                        <span>5</span>
                        <span>10</span>
                      </div>
                    </div>
                    
                    <Button
                      onClick={() => submitRating(artist.user_id)}
                      className="w-full bg-accent rounded-full hover:scale-105 transition-transform"
                      data-testid={`submit-rating-${artist.user_id}`}
                    >
                      <Save className="mr-2 h-4 w-4" />
                      Submit Rating
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default JudgingPanel;
